import { useEffect, useRef, useState } from 'react'
import { useFeature } from '../../store/features'

const INTERVAL_MS = 2000
const MAX_MEASUREMENT_RESULTS = 100
const TEST_TIMEOUT_RUNS = 15
const BYTES_IN_MB = 1048576

type PerformanceMemory = {
  usedJSHeapSize: number
  totalJSHeapSize: number
  jsHeapSizeLimit: number
}

type MemoryUsage = {
  usedMB: string
  totalMB: string
  limitMB: string
}

type MeasurementResult = {
  memoryUsage: MemoryUsage
  testRunDuration: number
  fps: number
}

declare global {
  interface Window {
    perfoMonitorResults?: MeasurementResult[]
  }
}

function getPerformanceMemory() {
  return (performance as Performance & { memory?: PerformanceMemory }).memory
}

function getMemoryUsage(): MemoryUsage {
  const memory = getPerformanceMemory()

  if (!memory) {
    return {
      usedMB: '0.0',
      totalMB: '0.0',
      limitMB: '0.0'
    }
  }

  return {
    usedMB: (memory.usedJSHeapSize / BYTES_IN_MB).toFixed(1),
    totalMB: (memory.totalJSHeapSize / BYTES_IN_MB).toFixed(1),
    limitMB: (memory.jsHeapSizeLimit / BYTES_IN_MB).toFixed(1)
  }
}

function runTimeoutTest() {
  const startedAt = performance.now()

  return new Promise<number>(resolve => {
    let runsLeft = TEST_TIMEOUT_RUNS

    function runNextTimeout() {
      if (runsLeft === 0) {
        resolve(performance.now() - startedAt)
        return
      }

      runsLeft -= 1
      setTimeout(runNextTimeout, 0)
    }

    runNextTimeout()
  })
}

export function usePerfoMonitor() {
  const [measurementResults, setMeasurementResults] = useState<MeasurementResult[]>([])
  const frameCount = useRef(0)
  const fpsMeasuredAt = useRef(performance.now())

  useEffect(() => {
    let isMounted = true
    let animationFrameId = 0

    function trackFrame() {
      frameCount.current += 1
      animationFrameId = requestAnimationFrame(trackFrame)
    }

    function getFps() {
      const measuredAt = performance.now()
      const elapsedMs = measuredAt - fpsMeasuredAt.current

      if (elapsedMs === 0) {
        return 0
      }

      const fps = Math.round((frameCount.current * 1000) / elapsedMs)
      frameCount.current = 0
      fpsMeasuredAt.current = measuredAt

      return fps
    }

    async function collectMeasurement() {
      const memoryUsage = getMemoryUsage()
      const testRunDuration = await runTimeoutTest()
      const fps = getFps()

      if (!isMounted) {
        return
      }

      setMeasurementResults(results =>
        [...results, { memoryUsage, testRunDuration, fps }].slice(-MAX_MEASUREMENT_RESULTS)
      )
    }

    animationFrameId = requestAnimationFrame(trackFrame)
    const intervalId = setInterval(collectMeasurement, INTERVAL_MS)

    return () => {
      isMounted = false
      cancelAnimationFrame(animationFrameId)
      clearInterval(intervalId)
    }
  }, [])

  return measurementResults
}

export function PerfoMonitor() {
  const measurementResults = usePerfoMonitor()

  useEffect(() => {
    window.perfoMonitorResults = measurementResults
  }, [measurementResults])

  return null
}

export const PerfoMonitorFeatureGate: React.FC = () => {
  const isPerfoMonitoringEnabled = useFeature('PERFORMANCE_MONITOR')

  return isPerfoMonitoringEnabled ? <PerfoMonitor /> : null
}
